
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import {
  generateQrCodeCardDataUrl,
  generateQrCodeCanvasDataUrl,
  QrCodeCardData,
  QrCodeMode,
  downloadQrCodeCard
} from '@/utils/qrCodeUtils'
import { AssignedCategoryProductSet } from './types'
import { ProductSetInfoKeyType, ProductCombinationType } from '@/__generated__/globalTypes'

@Component
export default class QrGenerator extends Vue {
  @Prop({ required: true, default: false }) show!: boolean
  @Prop({ required: true, default: QrCodeMode.SINGLE }) mode!: QrCodeMode
  @Prop({ required: true, default: '' }) productPackageId!: string
  @Prop({ required: true, default: undefined }) combinationType!: ProductCombinationType
  @Prop({ required: false, default: '' }) categoryName!: string
  @Prop({ required: true, default: null }) productPackageSet!:
    | AssignedCategoryProductSet
    | AssignedCategoryProductSet[]
    | null

  QrCodeMode = QrCodeMode
  qrCodeCardImgSrc = ''
  loading = false

  get internalProductPackageSet() {
    if (!this.productPackageSet) return []
    return Array.isArray(this.productPackageSet) ? this.productPackageSet : [this.productPackageSet]
  }

  get singleProductSet() {
    return this.internalProductPackageSet[0]?.productSet
  }

  get brandName() {
    return this.mode === QrCodeMode.SINGLE
      ? this.singleProductSet?.assignedProductSetInfos?.find((x) => x.key == ProductSetInfoKeyType.Brand)?.value ?? ''
      : ''
  }

  get seriesName() {
    return this.mode === QrCodeMode.SINGLE
      ? this.singleProductSet?.assignedProductSetInfos?.find((x) => x.key == ProductSetInfoKeyType.Series)?.value ?? ''
      : ''
  }

  get productSetName() {
    return this.singleProductSet?.name ?? ''
  }

  get qrCodeTitle() {
    switch (this.mode) {
      case QrCodeMode.SINGLE:
        return this.productSetName
      case QrCodeMode.COMBINATION:
        return `${this.dutchCombinationName} QR`
      default:
        return ''
    }
  }

  get dutchCombinationName() {
    return (
      this.$t(`global.combinations.${this.combinationType?.toString().toLowerCase()}`, 'nl').toString() + 'combinatie'
    )
  }

  @Watch('show')
  async onshowUpdate() {
    if (!this.show || !this.internalProductPackageSet.length || !this.productPackageId || !this.combinationType) return

    this.loading = true
    await this.generateQRCode().finally(() => {
      this.loading = false
    })
  }

  async generateQRCode() {
    const qrCodeImageSrc = await generateQrCodeCanvasDataUrl({
      mode: this.mode,
      productPackageId: this.productPackageId,
      combinationType: this.combinationType,
      productSetIds: this.internalProductPackageSet.map((pps) => pps.productSet.id)
    })

    const data: QrCodeCardData = {
      mode: this.mode,
      qrCodeCanvasDataUrl: qrCodeImageSrc
    }

    switch (this.mode) {
      case QrCodeMode.SINGLE:
        data.categoryName = this.categoryName
        data.productSetName = this.productSetName
        data.brandName = this.brandName
        data.seriesName = this.seriesName
        break
      case QrCodeMode.COMBINATION:
        data.combinationName = this.dutchCombinationName
        break
    }

    this.qrCodeCardImgSrc = await generateQrCodeCardDataUrl(data)
  }

  async downloadQRCode() {
    let filename = ''

    switch (this.mode) {
      case QrCodeMode.SINGLE:
        filename = `dbk-qr-${this.productSetName}.png`
        break
      case QrCodeMode.COMBINATION:
        filename = `dbk-qr-combi-${this.dutchCombinationName?.toLowerCase()}.png`
        break
    }

    downloadQrCodeCard(this.qrCodeCardImgSrc, filename)
    this.$emit('close')
  }
}
